export const CONTEXT_GLOBAL_TYPES: ReadonlySet<string> = new Set([
  "HOLDER",
  "EXECUTOR",
  "IMPERSONATING",
]);

// Titular (persona jurídica) legal data. Both variables (razón social /
// nombre comercial) share the LEGAL_INFO global_type and are distinguished by
// their `value`, exactly like COMPANY_FIELD.
export const isHolderLegalGlobalType = (globalType?: string): boolean =>
  globalType === "LEGAL_INFO";

// Titular data resolved by the global type itself, with no `value` payload:
// tipo-persona (PERSON_TYPE) and tipo-documento-identidad (DOCUMENT_TYPE).
export const HOLDER_VALUELESS_GLOBAL_TYPES: ReadonlySet<string> = new Set([
  "PERSON_TYPE",
  "DOCUMENT_TYPE",
]);

export const isHolderValuelessGlobalType = (globalType?: string): boolean =>
  !!globalType && HOLDER_VALUELESS_GLOBAL_TYPES.has(globalType);

export const getNodeIdForContextVariable = (
  globalType: string,
  value: string,
): string => {
  if (globalType === "IMPERSONATING") return "impersonation-variables";
  if (globalType === "EXECUTOR") {
    // Natural-person data lives in its own sub-group, mirroring the holder
    // grouping; contact fields stay in the representante base group.
    if (["first_name", "last_name", "name"].includes(value))
      return "representante-personal-variables";
    return "representante-variables";
  }
  if (isHolderValuelessGlobalType(globalType)) return "holder-base-variables";
  // COMPANY_FIELD (expedientes) and LEGAL_INFO (registro) both live here.
  if (isHolderLegalGlobalType(globalType)) return "holder-legal-variables";
  // HOLDER resolves to holder sub-group by value (accepts both legacy and new API values)
  if (["email", "phone", "type"].includes(value))
    return "holder-base-variables";
  if (
    [
      "first_name",
      "name",
      "last_name",
      "document_number",
      "birth_date",
    ].includes(value)
  )
    return "holder-personal-variables";
  return "holder-legal-variables";
};

export const mapBackendTypeToVariableType = (
  backendType: string,
): "string" | "number" | "date" | "boolean" | "array" => {
  switch (backendType.toUpperCase()) {
    case "STRING":
    case "TEXT":
      return "string";
    case "INTEGER":
    case "NUMBER":
    case "FLOAT":
    case "DOUBLE":
      return "number";
    case "DATE":
    case "DATETIME":
    case "YEAR":
      return "date";
    case "BOOLEAN":
    case "BOOL":
      return "boolean";
    case "ARRAY":
    case "FORM":
    case "MIXED":
      return "array";
    default:
      return "string";
  }
};

/**
 * Remove nodeId from expedient fields refs since BE will use output_name to resolve
 * Used for form expedient fields
 * @param nodeId
 * @returns nodeId without the -expedient-fieldId suffix
 */
export const mapExpedientNodeId = (nodeId?: string) => {
  if (!nodeId || typeof nodeId !== "string") {
    return nodeId;
  }

  if (nodeId.includes("-expedient-")) {
    const nodeIdParts = nodeId.split("-expedient-");
    if (nodeIdParts.length >= 1 && nodeIdParts[0]) {
      return nodeIdParts[0];
    }
  }

  return nodeId;
};

export const mapFieldTypeToVariableType = (
  type: string,
  multiple = false,
  selectType?: string,
):
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "array"
  | "file"
  | "files"
  | "select" => {
  if (type.toLowerCase() === "file" || type.toLowerCase() === "files") {
    return multiple || type.toLowerCase() === "files" ? "files" : "file";
  }
  if (multiple) {
    return "array";
  }
  switch (type.toLowerCase()) {
    case "text":
    case "textarea":
    case "email":
    case "phone":
    case "dui_nit": {
      return "string";
    }
    case "date":
    case "datetime":
    case "date_range":
    case "timestamp":
    case "datetime_range":
    case "year": {
      return "date";
    }
    case "float":
    case "integer": {
      return "number";
    }
    case "select": {
      if (selectType === "boolean") {
        return "boolean";
      }
      if (selectType === "number") {
        return "number";
      }
      return "string";
    }
    case "switch": {
      return "boolean";
    }
    case "file": {
      return "file";
    }
    default:
      return type.toLowerCase() as
        "string" | "number" | "boolean" | "date" | "array" | "file" | "files";
  }
};

export const mapVariableTypeToBackendType = (type: string): string => {
  switch (type) {
    case "string":
    case "regex":
      return "STRING";
    case "number":
    case "positive-integer":
      return "NUMBER";
    case "variable":
      return "MODEL";
    case "boolean":
      return "BOOLEAN";
    case "date":
    case "year":
      return "DATE";
    // Una tabla viaja como estructura tabular, pero el contrato de tipos
    // primitivos del backend no tiene TABLE: se envia como ARRAY y la
    // distincion la conserva `meta_type`, igual que hace `select`.
    case "array":
    case "table":
      return "ARRAY";
    case "file":
    case "files":
      return "DOCUMENT";
    default:
      return "STRING";
  }
};
